import type { Artifact } from '@deskworks/shared-types';
import { FileText, Globe, Image as ImageIcon, Maximize2, Paperclip } from 'lucide-react';
import { useState } from 'react';
import { artifactFile, FileViewer, FilePreview, hasPreview } from '@/components/files/file-preview';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { EmptyState } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatBytes } from '@/lib/format';
import { t, tc } from '@/lib/i18n';

export function ArtifactsSection({ artifacts }: { artifacts: Artifact[] }) {
  const [open, setOpen] = useState<string | null>(null);

  return (
    <Section icon={<Paperclip />} title={t('Artifacts')} description={t('Files the agent has created. Click one to open it large, download it or make it public.')}>
      {artifacts.length === 0 ? (
        <EmptyState compact title={t('No files')} description={t('When the agent creates files, you can download them here.')} />
      ) : (
        <ul className="-mx-5 -mb-5 divide-y border-t">
          {artifacts.map((a) => {
            const file = artifactFile(a);
            return (
              <li key={a.id} className="flex items-start gap-3 px-5 py-2.5">
                {file.mimeType.startsWith('image/') ? (
                  <ImageIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                ) : (
                  <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm font-medium">{a.filename}</span>
                    {a.visibility === 'public' && (
                      <Badge tone="green">
                        <Globe /> {t('Public')}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatBytes(a.size)} · {a.mimeType} · <RelativeTime value={a.createdAt} />
                    {a.type && a.type !== 'file' && ` · ${a.type}`}
                  </div>
                  {hasPreview(file) && <FilePreview file={file} />}
                </div>
                <Button variant="outline" size="sm" onClick={() => setOpen(a.id)}>
                  <Maximize2 /> {tc('verb', 'Open')}
                </Button>
                {open === a.id && <FileViewer file={file} open onOpenChange={(o) => !o && setOpen(null)} />}
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}
